import React, { useEffect, useMemo, useState } from "react";
import { TL } from "../i18n";
import client from "../lib/api";
import { toast } from "sonner";
import { num, money } from "../lib/holdingsMath";
import { Trash2, Plus } from "lucide-react";
import { AppraisalButton } from "./Appraisal";
import { CommitmentButton } from "./CapitalCall";

const PROFILES = ["Growth", "Income", "Capital Preservation"];
const II = "w-full rounded border border-black/15 bg-white/70 px-1.5 py-1 text-[11px] dark:border-white/15 dark:bg-black/30";
const LL = "text-[9px] uppercase tracking-wide opacity-55";

export default function ThirdPartyLedger({ items = [], reload, scope = null }) {
  const [edits, setEdits] = useState({});
  const [trades, setTrades] = useState([]);
  const [tradeFor, setTradeFor] = useState(null);

  const loadTrades = () => client.get("/fund_trades").then((r) => setTrades((r.data || []).map((x) => ({ id: x.id, ...(x.data || x) })))).catch(() => {});
  useEffect(() => { loadTrades(); }, []);
  useEffect(() => { setEdits((prev) => { const ids = new Set(items.map((i) => i.id)); const next = {}; Object.keys(prev).forEach((k) => { if (ids.has(k)) next[k] = prev[k]; }); return next; }); }, [items]);

  // keep holding.gross_value = held * nav so fund value folds into category/accumulated roll-ups
  const syncValue = async (fundId, nav) => {
    try {
      const r = await client.get("/fund_trades");
      const all = (r.data || []).map((x) => ({ id: x.id, ...(x.data || x) }));
      setTrades(all);
      const h = all.filter((t) => t.position_id === fundId).reduce((a, t) => a + (t.side === "buy" ? num(t.units) : -num(t.units)), 0);
      await client.put(`/holdings/${fundId}`, { data: { gross_value: h * num(nav) } }).catch(() => {});
      reload && reload();
    } catch (e) { /* ignore */ }
  };

  const view = (r) => ({ ...r, ...(edits[r.id] || {}) });
  const setField = (id, k, v) => setEdits((p) => ({ ...p, [id]: { ...(p[id] || {}), [k]: v } }));
  const persist = (id, k) => client.put(`/holdings/${id}`, { data: { [k]: (edits[id] || {})[k] } }).catch(() => toast.error("Could not save."));
  const persistNow = (id, k, v) => { setField(id, k, v); client.put(`/holdings/${id}`, { data: { [k]: v } }).catch(() => {}); };

  const tradesFor = (id) => trades.filter((t) => t.position_id === id);
  const held = (id) => tradesFor(id).reduce((a, t) => a + (t.side === "buy" ? num(t.units) : -num(t.units)), 0);
  const invested = (id) => tradesFor(id).reduce((a, t) => a + (t.side === "buy" ? num(t.units) * num(t.price) : -num(t.units) * num(t.price)), 0);
  const valueOf = (r) => held(r.id) * num(r.nav);
  const glOf = (r) => valueOf(r) - invested(r.id);
  const merFeeOf = (r) => valueOf(r) * num(r.mer) / 100; // est. annual cost drag

  const add = async (count) => {
    for (let i = 0; i < count; i++) await client.post("/holdings", { data: { category: "Alternative Assets", subcategory: "Mutual Funds", asset_class: "Fund", name: "", tp_profile: "Growth", nav: "", mer: "", ...(scope || {}) } });
    toast.success(`Added ${count} fund slot${count > 1 ? "s" : ""}.`); reload && reload();
  };
  const del = async (r) => {
    if (!window.confirm(`Remove ${r.name || "this fund"} and its purchases/sales?`)) return;
    await client.delete(`/holdings/${r.id}`);
    await Promise.all(tradesFor(r.id).map((t) => client.delete(`/fund_trades/${t.id}`).catch(() => {})));
    loadTrades(); reload && reload();
  };

  const totals = useMemo(() => items.reduce((a, raw) => { const r = view(raw); a.value += valueOf(r); a.gl += glOf(r); return a; }, { value: 0, gl: 0 }), [items, edits, trades]); // eslint-disable-line

  return (
    <div className="space-y-3" data-testid="fund-ledger">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[11px] uppercase tracking-wide opacity-55">{TL("Add fund:")}</span>
        <div className="flex overflow-hidden rounded border border-black/15 text-[11px] dark:border-white/15">
          {[1, 5, 10].map((c) => <button key={c} onClick={() => add(c)} data-testid={`fund-add-${c}`} className="px-2 py-1 font-semibold hover:bg-[var(--primary)] hover:text-white">+{c}</button>)}
        </div>
        <div className="ms-auto flex items-center gap-4 text-[11px]">
          <span>{TL("Value")} <b className="tabular-nums" data-testid="fund-total-value">{money(totals.value)}</b></span>
          <span>{TL("Gain / loss")} <b className={`tabular-nums ${totals.gl >= 0 ? "text-emerald-600" : "text-red-500"}`} data-testid="fund-total-gl">{totals.gl >= 0 ? "+" : ""}{money(totals.gl)}</b></span>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="rounded-lg border border-dashed border-black/15 py-6 text-center text-xs opacity-60 dark:border-white/15" data-testid="fund-empty">{TL("No funds yet. Use “+1 / +5 / +10” to add rows, fill in the fund details, then log purchases/sales.")}</div>
      ) : (
        <div className="space-y-2">
          {items.map((raw) => {
            const r = view(raw);
            const h = held(r.id), val = valueOf(r), gl = glOf(r);
            return (
              <div key={r.id} className="rounded-lg border border-black/10 bg-white/50 p-3 dark:border-white/10 dark:bg-white/[0.03]" data-testid={`fund-row-${r.id}`}>
                {/* ===== Line 1 ===== */}
                <div className="grid items-end gap-2 md:grid-cols-7">
                  <label className="block md:col-span-2"><span className={LL}>{TL("Fund name")}</span>
                    <input className={II} value={r.name || ""} onChange={(e) => setField(r.id, "name", e.target.value)} onBlur={() => persist(r.id, "name")} data-testid={`fund-name-${r.id}`} /></label>
                  <label className="block"><span className={LL}>{TL("Profile")}</span>
                    <select className={II} value={r.tp_profile || ""} onChange={(e) => persistNow(r.id, "tp_profile", e.target.value)} data-testid={`fund-profile-${r.id}`}>
                      {PROFILES.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select></label>
                  <label className="block"><span className={LL}>NAV ($)</span>
                    <input type="number" className={`${II} tabular-nums`} value={r.nav || ""} onChange={(e) => setField(r.id, "nav", e.target.value)} onBlur={() => { persist(r.id, "nav"); syncValue(r.id, (edits[r.id] || {}).nav ?? r.nav); }} data-testid={`fund-nav-${r.id}`} /></label>
                  <label className="block"><span className={LL}>{TL("MER (%)")}</span>
                    <input type="number" className={`${II} tabular-nums`} value={r.mer || ""} onChange={(e) => setField(r.id, "mer", e.target.value)} onBlur={() => persist(r.id, "mer")} data-testid={`fund-mer-${r.id}`} /></label>
                  <div><span className={LL}>{TL("# held")}</span>
                    <div className="rounded border border-black/10 bg-black/[0.03] px-1.5 py-1 text-[11px] tabular-nums dark:border-white/10 dark:bg-white/[0.04]" data-testid={`fund-held-${r.id}`}>{num(h).toLocaleString(undefined, { maximumFractionDigits: 4 })}</div></div>
                  <div><span className={LL}>{TL("Value")}</span>
                    <div className="rounded border border-black/10 bg-black/[0.03] px-1.5 py-1 text-[11px] font-semibold tabular-nums dark:border-white/10 dark:bg-white/[0.04]" data-testid={`fund-value-${r.id}`}>{money(val)}</div></div>
                </div>
                {/* gain/loss + delete row */}
                <div className="mt-1 flex items-center gap-3 text-[10px]">
                  <span>{TL("Gain / loss")} <b className={`tabular-nums ${gl >= 0 ? "text-emerald-600" : "text-red-500"}`} data-testid={`fund-gl-${r.id}`}>{gl >= 0 ? "+" : ""}{money(gl)}</b></span>
                  {num(r.mer) > 0 && <span className="opacity-70">Est. annual fee (MER {num(r.mer)}%) <b className="tabular-nums text-amber-600" data-testid={`fund-merfee-${r.id}`}>{money(merFeeOf(r))}</b></span>}
                  <div className="ms-auto flex items-center gap-1">
                    <CommitmentButton holding={r} reload={reload} />
                    <AppraisalButton holding={r} reload={reload} />
                    <button onClick={() => del(raw)} data-testid={`fund-del-${r.id}`} className="opacity-50 hover:text-red-500 hover:opacity-100"><Trash2 size={13} /></button>
                  </div>
                </div>

                {/* ===== Line 2: purchases & sales ===== */}
                <div className="mt-2 border-t border-black/5 pt-2 dark:border-white/5" data-testid={`fund-trades-${r.id}`}>
                  <div className="flex items-center gap-2">
                    <span className="text-[9px] uppercase tracking-wide opacity-55">{TL("Purchases & sales")}</span>
                    <button onClick={() => setTradeFor(tradeFor === r.id ? null : r.id)} data-testid={`fund-trade-btn-${r.id}`} className="rounded border border-black/15 px-1.5 py-0.5 text-[10px] hover:bg-[var(--primary)] hover:text-white dark:border-white/15"><Plus size={10} className="inline" /> {TL("Buy / Sell")}</button>
                    <span className="text-[10px] opacity-55">{tradesFor(r.id).length} logged</span>
                  </div>
                  {tradeFor === r.id && <TradePanel fund={r} onClose={() => setTradeFor(null)} onSaved={() => { syncValue(r.id, r.nav); setTradeFor(null); }} />}
                  {tradesFor(r.id).length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-1">
                      {tradesFor(r.id).map((t) => (
                        <span key={t.id} className={`flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] ${t.side === "buy" ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400" : "bg-red-500/10 text-red-700 dark:text-red-400"}`} data-testid={`fund-trade-chip-${t.id}`}>
                          {t.side === "buy" ? "Buy" : "Sell"} {num(t.units)} @ {money(t.price)} · {t.date}
                          <button onClick={async () => { await client.delete(`/fund_trades/${t.id}`); syncValue(r.id, r.nav); }} className="opacity-60 hover:opacity-100"><Trash2 size={9} /></button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function TradePanel({ fund, onClose, onSaved }) {
  const [side, setSide] = useState("buy");
  const [units, setUnits] = useState("");
  const [price, setPrice] = useState(fund.nav ? String(fund.nav) : "");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const save = async () => {
    if (!(num(units) > 0) || !(num(price) > 0)) return toast.error("Enter units and price.");
    try {
      await client.post("/fund_trades", { data: { position_id: fund.id, side, units: num(units), price: num(price), date } });
      toast.success(`${side === "buy" ? "Purchased" : "Sold"} ${units} units of ${fund.name || "fund"}.`);
      onSaved();
    } catch (e) { toast.error("Could not save."); }
  };
  return (
    <div className="mt-2 flex flex-wrap items-end gap-2 rounded-lg border border-[var(--primary)]/40 bg-[var(--primary)]/5 p-2" data-testid={`fund-trade-panel-${fund.id}`}>
      <select value={side} onChange={(e) => setSide(e.target.value)} data-testid={`fund-trade-side-${fund.id}`} className="rounded border border-black/15 px-1.5 py-1 text-[11px] dark:border-white/15 dark:bg-black/30"><option value="buy">{TL("Purchase")}</option><option value="sell">{TL("Sale")}</option></select>
      <input type="number" value={units} onChange={(e) => setUnits(e.target.value)} placeholder={TL("units")} data-testid={`fund-trade-units-${fund.id}`} className={`${II} w-24`} />
      <input type="number" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="price $" data-testid={`fund-trade-price-${fund.id}`} className={`${II} w-28`} />
      <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid={`fund-trade-date-${fund.id}`} className={`${II} w-32`} />
      <button onClick={save} data-testid={`fund-trade-save-${fund.id}`} className="rounded bg-[var(--primary)] px-2 py-1 text-[11px] font-semibold text-white">{TL("Save")}</button>
      <button onClick={onClose} className="text-[11px] opacity-60 hover:opacity-100">{TL("Cancel")}</button>
    </div>
  );
}
